/**
 * Page swipe — interface "vu / pas vu" pour recenser les espèces aperçues.
 */

import { el, clear } from '../js/utils.js';
import { t } from '../js/i18n.js';
import { listSpecies, formatSize, displayName } from '../js/species.js';
import {
  recordSighting,
  undoSighting,
  getSwipedIds,
  countSightings,
  resetAllSightings,
} from '../js/sightings.js';
import { renderHeader } from '../components/header.js';
import { renderFooter } from '../components/footer.js';
import { iconEye, iconEyeSlash, iconQuestionCircle, iconRefresh } from '../components/icons.js';
import { showSnackbar, dismissSnackbar } from '../components/snackbar.js';

const SWIPE_THRESHOLD_RATIO = 0.28;
const MAX_ROTATION = 14;
const CATEGORIES = ['fish', 'mollusk', 'coral', 'crustacean', 'echinoderm', 'plant', 'mammal', 'turtle', 'other'];

// Préserve l'espèce courante et le choix en cours entre deux navigations
let savedSpeciesId = null;
let savedChoice = null;

export async function renderSwipe(root, ctx = {}) {
  clear(root);
  document.body.classList.add('page-swipe');

  const view = el(
    'main',
    { class: 'swipe-page' },

    el(
      'div',
      { class: 'swipe-content' },

    el(
      'header',
      { class: 'swipe-header' },
      el('div', { class: 'swipe-progress', id: 'progress' }),
      el(
        'button',
        {
          class: 'swipe-reset',
          id: 'reset-btn',
          title: t('swipe.reset.label'),
          'aria-label': t('swipe.reset.label'),
        },
        iconRefresh()
      )
    ),

    el(
      'div',
      { class: 'swipe-categories', id: 'swipe-categories' },
      el('button', { class: 'category-pill is-active', 'data-category': '' }, t('category.all')),
      ...CATEGORIES.map(cat =>
        el('button', { class: 'category-pill', 'data-category': cat }, t(`category.${cat}`))
      )
    ),

    el(
      'section',
      { class: 'swipe-stage' },
      el('div', { class: 'swipe-card-slot', id: 'card-slot' }),
      el('div', { class: 'swipe-empty', id: 'empty', hidden: 'true' })
    ),

    el(
      'footer',
      { class: 'swipe-actions' },
      el(
        'button',
        {
          class: 'swipe-btn swipe-btn-no',
          id: 'btn-no',
          'aria-pressed': 'false',
          'aria-label': t('swipe.btn.no.label'),
          title: t('swipe.btn.no.title'),
        },
        iconEyeSlash(),
        el('span', { class: 'swipe-btn-label' }, t('swipe.btn.no.text'))
      ),
      el(
        'button',
        {
          class: 'swipe-btn swipe-btn-doubt',
          id: 'btn-doubt',
          'aria-pressed': 'false',
          'aria-label': t('swipe.btn.doubt.label'),
          title: t('swipe.btn.doubt.title'),
        },
        iconQuestionCircle(),
        el('span', { class: 'swipe-btn-label' }, t('swipe.btn.doubt.text'))
      ),
      el(
        'button',
        {
          class: 'swipe-btn swipe-btn-yes',
          id: 'btn-yes',
          'aria-pressed': 'false',
          'aria-label': t('swipe.btn.yes.label'),
          title: t('swipe.btn.yes.title'),
        },
        iconEye(),
        el('span', { class: 'swipe-btn-label' }, t('swipe.btn.yes.text'))
      )
    )

    ) // swipe-content
  );

  root.append(renderHeader(), view, renderFooter());

  // ---------- État ----------
  const slotEl = view.querySelector('#card-slot');
  const progressEl = view.querySelector('#progress');
  const emptyEl = view.querySelector('#empty');
  const btnYes = view.querySelector('#btn-yes');
  const btnNo = view.querySelector('#btn-no');
  const btnDoubt = view.querySelector('#btn-doubt');

  let queue = [];
  let currentCard = null;
  let isAnimating = false;
  let currentChoice = null;
  let advanceTimer = null;
  let lastActionedSpecies = null;

  const zoneId = ctx.query?.zone_id ?? null;
  let selectedCategory = null;

  const categoriesBar = view.querySelector('#swipe-categories');
  categoriesBar.addEventListener('click', e => {
    const pill = e.target.closest('.category-pill');
    if (!pill) return;
    categoriesBar.querySelectorAll('.category-pill').forEach(p => p.classList.remove('is-active'));
    pill.classList.add('is-active');
    selectedCategory = pill.dataset.category || null;
    loadQueue();
  });

  async function loadQueue() {
    const all = await listSpecies({ zoneId, category: selectedCategory });
    const swiped = getSwipedIds();
    const pending = all.filter((sp) => !swiped.has(sp.id));

    // Restaure l'espèce précédemment affichée en tête de queue
    const restoreId = savedSpeciesId;
    const restoreChoice = savedChoice;
    savedSpeciesId = null;
    savedChoice = null;

    const idx = restoreId ? pending.findIndex((sp) => sp.id === restoreId) : -1;
    if (idx > 0) {
      const [sp] = pending.splice(idx, 1);
      queue = [sp, ...shuffle(pending)];
    } else {
      queue = shuffle(pending);
    }

    updateProgress();
    if (queue.length === 0) {
      showEmptyState();
    } else {
      renderCurrent(); // reset currentChoice → null
      if (restoreChoice && queue[0]?.id === restoreId) {
        currentChoice = restoreChoice;
        updateButtonStates();
      }
    }
  }

  function updateProgress() {
    const counts = countSightings();
    progressEl.innerHTML = '';
    if (counts.total === 0) {
      progressEl.append(
        el('span', { class: 'muted' }, t('swipe.progress.total', { count: queue.length }))
      );
      return;
    }
    progressEl.append(
      el('span', {}, `${counts.seen} `),
      el('span', { class: 'swipe-progress-label' }, t('swipe.progress.seenLabel', { count: counts.seen })),
      el('span', { class: 'swipe-progress-sep' }, ' · '),
      el('span', { class: 'muted' }, t('swipe.progress.remaining', { count: queue.length }))
    );
  }

  function showEmptyState() {
    slotEl.hidden = true;
    emptyEl.hidden = false;
    emptyEl.innerHTML = '';
    const counts = countSightings();
    const stats = [
      t('swipe.empty.seen', { count: counts.seen }),
      t('swipe.empty.doubt', { count: counts.doubt }),
      t('swipe.empty.skipped', { count: counts.skipped }),
    ].join(' · ');

    const emptyTitle = selectedCategory
      ? t('swipe.empty.title_filtered', { category: t(`category.${selectedCategory}`) })
      : t('swipe.empty.title');

    emptyEl.append(
      el('p', { class: 'eyebrow', style: { color: 'var(--coral)' } }, t('swipe.empty.eyebrow')),
      el('h2', { class: 'swipe-empty-title' }, emptyTitle),
      el('p', { class: 'muted swipe-empty-stats' }, stats),
      el(
        'div',
        { class: 'swipe-empty-actions' },
        el('a', { class: 'btn btn-primary', href: '#/species' }, t('swipe.empty.catalog')),
        el(
          'button',
          {
            class: 'btn btn-ghost',
            onclick: () => {
              if (confirm(t('swipe.confirm.reset'))) {
                resetAllSightings();
                loadQueue();
              }
            },
          },
          t('swipe.empty.restart')
        )
      )
    );
  }

  function updateButtonStates() {
    btnYes.setAttribute('aria-pressed', currentChoice === 'seen' ? 'true' : 'false');
    btnNo.setAttribute('aria-pressed', currentChoice === 'skipped' ? 'true' : 'false');
    btnDoubt.setAttribute('aria-pressed', currentChoice === 'doubt' ? 'true' : 'false');
  }

  function handleButtonChoice(status) {
    if (isAnimating || queue.length === 0 || !currentCard) return;

    const species = queue[0];

    // Re-clic sur le même bouton : annulation avant avance
    if (currentChoice === status) {
      clearTimeout(advanceTimer);
      advanceTimer = null;
      currentChoice = null;
      updateButtonStates();
      dismissSnackbar();
      return;
    }

    // Nouveau choix : remplace un éventuel timer précédent
    if (advanceTimer !== null) {
      clearTimeout(advanceTimer);
      advanceTimer = null;
    }

    currentChoice = status;
    updateButtonStates();

    const btn = status === 'seen' ? btnYes : status === 'skipped' ? btnNo : btnDoubt;
    btn.classList.add('is-confirming');
    btn.addEventListener('animationend', () => btn.classList.remove('is-confirming'), { once: true });

    // committed = true une fois que triggerAction a été appelé (recordSighting effectué)
    let committed = false;

    const choiceKey = status === 'seen' ? 'seen' : status === 'skipped' ? 'skipped' : 'doubt';
    showSnackbar({
      message: t('swipe.snackbar.marked', {
        name: displayName(species),
        choice: t(`swipe.snackbar.choice.${choiceKey}`),
      }),
      actionLabel: t('snackbar.undo'),
      onAction: () => {
        if (!committed) {
          // Carte encore présente : annuler le timer, rien en DB
          clearTimeout(advanceTimer);
          advanceTimer = null;
          currentChoice = null;
          updateButtonStates();
        } else {
          // Carte déjà committée : undoSighting + réinsertion en tête de queue
          undoSighting(species.id);
          if (queue[0]?.id !== species.id) {
            queue.unshift(species);
          }
          renderCurrent();
        }
      },
      duration: 4000,
    });

    advanceTimer = setTimeout(() => {
      advanceTimer = null;
      committed = true;
      triggerAction(status);
    }, 380);
  }

  // ---------- Rendu d'une carte ----------
  function renderCurrent() {
    slotEl.hidden = false;
    emptyEl.hidden = true;
    slotEl.innerHTML = '';
    isAnimating = false;
    currentChoice = null;
    lastActionedSpecies = null;
    if (advanceTimer !== null) {
      clearTimeout(advanceTimer);
      advanceTimer = null;
    }
    updateButtonStates();

    if (queue.length === 0) {
      showEmptyState();
      return;
    }

    const sp = queue[0];
    const card = buildCard(sp);
    card.classList.add('is-entering');
    slotEl.append(card);
    currentCard = card;
    attachGestures(card, sp);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        card.classList.remove('is-entering');
      });
    });
  }

  function buildCard(sp) {
    const name = displayName(sp);
    return el(
      'article',
      { class: 'swipe-card', 'data-species-id': sp.id },

      el(
        'div',
        { class: 'swipe-card-photo' },
        sp.primary_image_url
          ? el('img', { src: sp.primary_image_url, alt: '', draggable: 'false' })
          : el('div', { class: 'swipe-card-photo-empty' }, '—'),
        el('span', { class: 'swipe-badge swipe-badge-yes' }, t('swipe.badge.yes')),
        el('span', { class: 'swipe-badge swipe-badge-no' }, t('swipe.badge.no')),
        sp.danger_level > 0 &&
          el(
            'span',
            {
              class: `swipe-card-danger danger-${sp.danger_level}`,
              title: t('swipe.card.danger', { level: sp.danger_level }),
            },
            '⚠'
          )
      ),

      el(
        'div',
        { class: 'swipe-card-info' },
        el('h2', { class: 'swipe-card-name' }, name),
        el(
          'p',
          { class: 'swipe-card-sci' },
          el('em', {}, sp.scientific_name),
          formatSize(sp.max_size_cm)
            ? el('span', { class: 'swipe-card-size' }, ` · ${formatSize(sp.max_size_cm)}`)
            : null
        ),
        el(
          'a',
          {
            class: 'swipe-card-more',
            href: `#/species/${sp.id}?from=swipe`,
            onclick: (e) => e.stopPropagation(),
          },
          t('swipe.card.more')
        )
      )
    );
  }

  // ---------- Gestures ----------
  function attachGestures(card, species) {
    let startX = 0;
    let startY = 0;
    let currentX = 0;
    let dragging = false;
    let pointerId = null;
    let isHorizontal = null;

    const onPointerDown = (e) => {
      if (isAnimating) return;
      if (e.target.closest('a')) return;
      dragging = true;
      pointerId = e.pointerId;
      startX = e.clientX;
      startY = e.clientY;
      currentX = 0;
      isHorizontal = null;
      try { card.setPointerCapture(pointerId); } catch {}
    };

    const onPointerMove = (e) => {
      if (!dragging || e.pointerId !== pointerId) return;
      const dx = e.clientX - startX;
      const dy = e.clientY - startY;

      if (isHorizontal === null) {
        if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
        isHorizontal = Math.abs(dx) > Math.abs(dy);
        if (!isHorizontal) {
          dragging = false;
          try { card.releasePointerCapture(pointerId); } catch {}
          return;
        }
        card.classList.add('is-dragging');
      }

      currentX = dx;
      applyTransform(card, currentX);
    };

    const onPointerUp = (e) => {
      if (!dragging) return;
      dragging = false;
      card.classList.remove('is-dragging');
      try { card.releasePointerCapture(pointerId); } catch {}

      if (isHorizontal !== true) return;

      const threshold = window.innerWidth * SWIPE_THRESHOLD_RATIO;
      if (currentX > threshold) {
        commitSwipe(card, species, 'seen', 1);
      } else if (currentX < -threshold) {
        commitSwipe(card, species, 'skipped', -1);
      } else {
        card.style.transition = 'transform 320ms var(--ease-out)';
        card.style.transform = '';
        card.style.removeProperty('--swipe-progress');
        card.addEventListener('transitionend', () => { card.style.transition = ''; }, { once: true });
      }
    };

    card.addEventListener('pointerdown', onPointerDown);
    card.addEventListener('pointermove', onPointerMove);
    card.addEventListener('pointerup', onPointerUp);
    card.addEventListener('pointercancel', onPointerUp);
  }

  function applyTransform(card, dx) {
    const rotation = (dx / window.innerWidth) * MAX_ROTATION;
    const opacity = Math.min(Math.abs(dx) / (window.innerWidth * SWIPE_THRESHOLD_RATIO), 1);
    card.style.transform = `translateX(${dx}px) rotate(${rotation}deg)`;
    card.style.setProperty('--swipe-progress', String(dx > 0 ? opacity : -opacity));
  }

  function commitSwipe(card, species, status, direction) {
    isAnimating = true;
    const exitX = direction * window.innerWidth * 1.4;
    card.style.transition = 'transform 360ms var(--ease-out), opacity 360ms ease-out';
    card.style.transform = `translateX(${exitX}px) rotate(${direction * MAX_ROTATION * 1.2}deg)`;
    card.style.opacity = '0';
    card.style.pointerEvents = 'none';

    recordSighting(species.id, status, { region: 'mediterranee' });

    setTimeout(() => {
      queue.shift();
      updateProgress();
      renderCurrent();
    }, 320);
  }

  // ---------- Boutons et clavier ----------
  function triggerAction(status) {
    if (isAnimating || queue.length === 0 || !currentCard) return;
    const species = queue[0];

    if (status === 'doubt') {
      isAnimating = true;
      currentCard.style.transition = 'transform 280ms var(--ease-out), opacity 280ms ease-out';
      currentCard.style.transform = 'scale(0.92) translateY(-12px)';
      currentCard.style.opacity = '0';
      currentCard.style.pointerEvents = 'none';
      recordSighting(species.id, 'doubt', { region: 'mediterranee' });
      setTimeout(() => {
        queue.shift();
        updateProgress();
        renderCurrent();
      }, 240);
    } else {
      const dir = status === 'seen' ? 1 : -1;
      commitSwipe(currentCard, species, status, dir);
    }
  }

  btnYes.addEventListener('click', () => handleButtonChoice('seen'));
  btnNo.addEventListener('click', () => handleButtonChoice('skipped'));
  btnDoubt.addEventListener('click', () => handleButtonChoice('doubt'));

  view.querySelector('#reset-btn').addEventListener('click', () => {
    if (confirm(t('swipe.confirm.fullReset'))) {
      resetAllSightings();
      loadQueue();
    }
  });

  const onKey = (e) => {
    if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
    if (e.key === 'ArrowRight') triggerAction('seen');
    else if (e.key === 'ArrowLeft') triggerAction('skipped');
    else if (e.key === ' ' || e.key === 'ArrowUp') {
      e.preventDefault();
      triggerAction('doubt');
    }
  };
  window.addEventListener('keydown', onKey);

  const cleanup = () => {
    // Sauvegarder l'espèce courante pour la restaurer au retour
    if (queue.length > 0) {
      savedSpeciesId = queue[0].id;
      // Sauvegarder le choix seulement s'il n'a pas encore été commité (timer encore en attente)
      savedChoice = advanceTimer !== null ? currentChoice : null;
    }
    // Annuler le timer d'avance pour éviter un recordSighting fantôme après navigation
    if (advanceTimer !== null) {
      clearTimeout(advanceTimer);
      advanceTimer = null;
    }
    dismissSnackbar();
    window.removeEventListener('keydown', onKey);
    window.removeEventListener('hashchange', cleanup);
    document.body.classList.remove('page-swipe');
  };
  window.addEventListener('hashchange', cleanup);

  await loadQueue();
}

// ---------- Helpers ----------

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}
